import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { capitalizeName } from 'src/app/utils/name-format.util';
import {
  getLeadSourceImg,
  isLeadSourceImage,
} from 'src/app/utils/lead-source-icon.util';

@Component({
  selector: 'app-content-info',
  templateUrl: './content-info.component.html',
  styleUrls: ['./content-info.component.scss'],
})
export class ContentInfoComponent {
  mobileInfoVisible: any;
  @Input() element: any;
  @Input() infoArray: any;
  @Input() current_id_hover = '';
  @Input() id = '';
  @Input() title: any;
  @Output() hoverChange = new EventEmitter<string>();
  hoveredRow: any;
  topPosition = 0;
  leftPosition = 0;
  isOpening = false;

  get isMobileView(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  toggleMobileInfo(id: any) {
    if (!this.isMobileView) {
      return;
    }
    this.mobileInfoVisible = this.mobileInfoVisible === id ? null : id;
  }

  @HostListener('window:resize')
  onResize(): void {
    if (!this.isMobileView) {
      this.mobileInfoVisible = false;
    }
  }

  handleShowInfo(element: any, event: any, id: any) {
    if (this.isMobileView) {
      this.toggleMobileInfo(id);
      return;
    }
    this.showInfo(element, event);
  }

  handleShowInfoOnHover(element: any, event: MouseEvent, _id: any): void {
    if (this.isMobileView) {
      return;
    }
    this.showInfo(element, event);
  }

  onPopupMouseEnter(): void {
    this.hoverChange.emit(this.id);
  }

  hideInfo(): void {
    this.hoverChange.emit('');
    this.mobileInfoVisible = false;
  }

  showInfo(row: any, event: MouseEvent): void {
    this.isOpening = true;
    this.hoveredRow = row;
    this.positionPopup(event);
    this.hoverChange.emit(this.id);

    setTimeout(() => {
      this.isOpening = false;
    }, 500);
  }

  private positionPopup(event: MouseEvent): void {
    const POPUP_W = 340;
    const POPUP_H = 420;
    const MARGIN = 12;

    let x = event.clientX + 20;
    let y = event.clientY - 10;

    if (x + POPUP_W > window.innerWidth - MARGIN) {
      x = event.clientX - POPUP_W - 10;
    }
    if (y + POPUP_H > window.innerHeight - MARGIN) {
      y = window.innerHeight - POPUP_H - MARGIN;
    }
    if (y < MARGIN) {
      y = MARGIN;
    }

    this.leftPosition = x;
    this.topPosition = y;
  }

  onMouseMove(_row: any, _event: MouseEvent): void {
    this.hoverChange.emit(this.id);
  }

  hideInfoClickOutside(): void {
    if (this.current_id_hover === this.id && !this.isOpening) {
      this.hoverChange.emit('');
      this.isOpening = false;
    } else {
      setTimeout(() => {
        this.isOpening = false;
      }, 100);
    }
  }

  get badgeClass(): string {
    const status =
      this.element?.['lead_status'] ||
      this.element?.fields?.lead_status ||
      this.element?.fields?.contact_stage;
    if (status === 'Closed - Won') {
      return 'won';
    }
    if (status === 'Closed - Lost') {
      return 'lost';
    }
    if (status) {
      return 'chip';
    }
    return 'chip-neutral';
  }

  get badgeLabel(): string {
    return (
      this.element?.['lead_status'] ||
      this.element?.fields?.lead_status ||
      this.element?.fields?.contact_stage ||
      this.element?.lead_source ||
      this.entityTypeLabel
    );
  }

  get entityTypeLabel(): string {
    const title = String(this.title || '').toLowerCase();
    if (title.includes('contact')) {
      return 'Contact';
    }
    if (title.includes('deal')) {
      return 'Deal';
    }
    if (title.includes('account') || title.includes('company')) {
      return 'Account';
    }
    if (title.includes('quote')) {
      return 'Quote';
    }
    if (title.includes('invoice')) {
      return 'Invoice';
    }
    return 'Record';
  }

  get entityIdLabel(): string {
    const title = String(this.title || '').toLowerCase();
    if (title.includes('contact')) {
      return 'Contact Id';
    }
    if (title.includes('deal')) {
      return 'Deal Id';
    }
    if (title.includes('account') || title.includes('company')) {
      return 'Account Id';
    }
    return 'Id';
  }

  get entityId(): string {
    return String(
      this.id ||
        this.element?.contact_id ||
        this.element?.deal_id ||
        this.element?.account_id ||
        this.element?.id ||
        ''
    );
  }

  get visibleFields(): any[] {
    return (this.infoArray || []).filter((item: any) => this.hasFieldValue(item));
  }

  hasText(value: unknown): boolean {
    return value != null && String(value).trim() !== '' && String(value) !== 'N/A';
  }

  hasFieldValue(item: { key: string; value: string }): boolean {
    return this.hasText(this.getFieldValue(item));
  }

  getFieldIcon(key: string): string {
    const iconMap: Record<string, string> = {
      first_name: 'fa-user',
      last_name: 'fa-user',
      email: 'fa-envelope',
      secondary_email: 'fa-envelope',
      phone: 'fa-phone',
      mobile: 'fa-mobile',
      fax: 'fa-fax',
      title: 'fa-briefcase',
      department: 'fa-building',
      lead_source: 'fa-bullhorn',
      source: 'fa-bullhorn',
      lead_status: 'fa-flag',
      contact_stage: 'fa-flag',
      description: 'fa-align-left',
      dob: 'fa-birthday-cake',
      createdAt: 'fa-calendar',
      updatedAt: 'fa-clock',
      Owner: 'fa-user-tie',
      owner: 'fa-user-tie',
      account_name: 'fa-building',
      ParentAccount: 'fa-sitemap',
      billing_address: 'fa-map-marker',
      shipping_address: 'fa-truck',
      website: 'fa-globe',
      linkedin: 'fa-linkedin',
    };
    return iconMap[key] || 'fa-circle-dot';
  }

  getFieldValue(item: { key: string; value: string }): string {
    const key = item?.key;
    if (!key || !this.element) {
      return '';
    }

    switch (key) {
      case 'Owner':
      case 'owner':
        return this.getAccountOwner(this.element['user']);
      case 'billing_address':
        return this.formatShippingAddress(this.element['billing_address']);
      case 'shipping_address':
        return this.formatShippingAddress(this.element['shipping_address']);
      case 'ParentAccount':
        return (
          this.element['ParentAccount']?.account_name ||
          this.element['parent_account']?.account_name ||
          ''
        );
      case 'account_name':
        return (
          this.element['account']?.account_name ||
          this.element['account_name'] ||
          ''
        );
      case 'createdAt':
      case 'updatedAt':
        return this.formatDate(this.element[key]);
      case 'first_name':
      case 'last_name':
        return capitalizeName(String(this.element[key] || '').trim());
      case 'source':
      case 'lead_source':
        return String(
          this.element['lead_source'] ||
            this.element['source'] ||
            this.element?.fields?.lead_source ||
            this.element?.fields?.source ||
            ''
        ).trim();
      case 'lead_status':
      case 'contact_stage':
        return String(
          this.element['lead_status'] ||
            this.element?.fields?.lead_status ||
            this.element?.fields?.contact_stage ||
            ''
        ).trim();
      default: {
        const direct = this.element[key];
        if (this.hasText(direct)) {
          return String(direct).trim();
        }
        const custom = this.element?.fields?.[key];
        if (this.hasText(custom)) {
          return String(custom).trim();
        }
        return '';
      }
    }
  }

  isSourceField(key?: string): boolean {
    return key === 'lead_source' || key === 'source';
  }

  isSourceImage(source: string): boolean {
    return isLeadSourceImage(source);
  }

  getSourceImg(source: string): string {
    return getLeadSourceImg(source);
  }

  formatDate(value: unknown): string {
    if (!value) {
      return '';
    }
    const date = new Date(value as string | number | Date);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  formatShippingAddress(address: any): string {
    if (!address || Object.keys(address).length === 0) {
      return '';
    }

    const parts = [
      address.street || '',
      address.city || '',
      address.state || '',
      address.postal_code || '',
      address.country || '',
    ];

    return parts.filter((part) => part.trim() !== '').join(', ');
  }

  getAccountOwner(user: any): string {
    const name = [user?.fname, user?.lname].filter(Boolean).join(' ');
    return name || '';
  }
}
